import { useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';

/**
 * Hook that implements a task scheduler that's helpful
 * when working with sequencing CSS transition updates
 */
export function useTransitionTasks() {
  const timeout = useRef<number | NodeJS.Timeout | undefined>(undefined);
  const tasks = useRef<(() => void)[]>([]);
  const lock = useRef(false);

  /**
   * Run a function in the next frame
   *
   * Guaranteed to not run in the current frame
   * (as opposed to requestAnimationFrame in some browsers)
   *
   * For scheduling tasks use `pushTask` instead (which internally
   * uses this, but also manages the tasks). `runNextFrame` is unmanaged.
   */
  const runNextFrame = useCallback(
    (cb: () => void) => setTimeout(() => requestAnimationFrame(cb), 1),
    []
  );

  /**
   * Pushes a task into the managed task queue.
   *
   * Tasks in the queue are guaranteed to run in order and
   * guaranteed to run one per frame. React updates are also flushed
   * in-between each task.
   *
   * Note: does not accept new tasks when the lock is set to true.
   */
  const pushTask = useCallback(
    (task: () => void) => {
      if (lock.current) return;

      tasks.current.push(task);

      if (timeout.current === undefined) {
        const cb = { f: null as unknown as () => void };

        cb.f = () => {
          const [task] = tasks.current.splice(0, 1);
          if (task) {
            flushSync(task);
            timeout.current = runNextFrame(cb.f);
          } else {
            timeout.current = undefined;
          }
        };

        timeout.current = runNextFrame(cb.f);
      }
    },
    [runNextFrame]
  );

  /**
   * Clears all tasks in the managed task queue.
   *
   * Note: works even if the lock is set to true.
   */
  const clearTasks = useCallback(() => {
    tasks.current = [];
    if (timeout.current !== undefined) {
      clearTimeout(timeout.current);
      timeout.current = undefined;
    }
  }, []);

  /**
   * Returns whether the lock is set or not
   */
  const areTasksLocked = useCallback(() => lock.current, []);

  /**
   * Locks/unlocks the task queue
   */
  const setTasksLocked = useCallback(
    (locked: boolean) => {
      lock.current = locked;
      if (locked) {
        clearTasks();
      }
    },
    [clearTasks]
  );

  return { pushTask, clearTasks, areTasksLocked, setTasksLocked, runNextFrame };
}
